import { SqliteObjectWrapper } from '../sqlite-wrapper/sqlite-object-wrapper';

/**
 * Esquema de la base offline del usuario (`${dni}.db`): las migraciones viven acá, numeradas, y cada
 * una corre una sola vez por base.
 *
 * La versión se guarda en una tabla propia y no en `PRAGMA user_version`, porque ese pragma lo usa
 * `@capacitor-community/sqlite` para sus propias migraciones (`createConnection(..., version, ...)`).
 *
 * Hay usuarios con cursos descargados y peticiones pendientes en su base: una migración solo agrega
 * (columnas, tablas). Nunca borra ni renombra.
 */

/** Lo único que necesita una migración: ejecutar SQL. */
export type SqlExecutor = Pick<SqliteObjectWrapper, 'executeSql'>;

export interface OfflineMigration {
  version: number;
  description: string;
  up(db: SqlExecutor): Promise<void>;
}

export const SCHEMA_VERSION_TABLE = 'offline_schema_version';

export async function tableExists(db: SqlExecutor, table: string): Promise<boolean> {
  const res = await db.executeSql(`SELECT name FROM sqlite_master WHERE type='table' AND name=?`, [table]);
  return res.rows.length > 0;
}

export async function columnExists(db: SqlExecutor, table: string, column: string): Promise<boolean> {
  const res = await db.executeSql(`PRAGMA table_info(${table})`, []);
  for (let i = 0; i < res.rows.length; i++) {
    if (res.rows.item(i).name === column) return true;
  }
  return false;
}

/** Agrega la columna solo si la tabla existe y todavía no la tiene. */
export async function addColumnIfMissing(
  db: SqlExecutor,
  table: string,
  column: string,
  type: string
): Promise<void> {
  if (!(await tableExists(db, table))) return;
  if (await columnExists(db, table, column)) return;
  await db.executeSql(`ALTER TABLE ${table} ADD COLUMN ${column} ${type}`, []);
}

/**
 * Las tablas que crea la descarga de un curso. El orden de las columnas es el de los `INSERT ... VALUES`
 * posicionales de los repositorios: no se reordena ni se inserta una columna en el medio.
 * `evaluations` y `surveys` llevan clave primaria (la migración 4 se la pone a las bases viejas): sin ella,
 * cada re-descarga apilaba otra fila y se leía la primera, que era la más vieja.
 */
export const OFFLINE_TABLES = {
  courses: (name = 'courses') => `CREATE TABLE IF NOT EXISTS ${name} (
    id integer primary key,
    orden integer,
    nombre text,
    descripcion text,
    imagen text,
    disponible integer,
    status text,
    temas_asignados integer,
    temas_completados integer,
    porcentaje text,
    registro_capacitacion_is_active int,
    registro_capacitacion_path text,
    registro_capacitacion_url text,
    encuesta int,
    encuesta_habilitada int,
    encuesta_id int,
    encuesta_resuelta int,
    mod_evaluaciones text,
    scheduled_activation text,
    tarea string,
    not_allowed_screenshot int,
    schoolName text,
    schoolId integer,
    registro_personalizado_is_active int,
    registro_personalizado_path text,
    registro_personalizado_url text,
    format text
  )`,
  topics: (name = 'topics') => `CREATE TABLE IF NOT EXISTS ${name} (
    id integer primary key,
    contenido text,
    disponible integer,
    estado_tema text,
    estado_tema_str text,
    evaluable text,
    intentos_restantes text,
    media_not_embed text,
    media_topic_progress text,
    mod_evaluaciones text,
    requirements text,
    tags text,
    nombre text,
    nota text,
    nota_sistema_nombre text,
    nota_sistema_valor integer,
    review_all_duration_media integer,
    tipo_ev text,
    open_evaluation_button text,
    course_id integer,
    topic_index integer
  )`,
  media: (name = 'media') => `CREATE TABLE IF NOT EXISTS ${name} (
    id integer primary key,
    downloadable integer,
    embed integer,
    full_path text,
    ia_convert integer,
    last_media_duration integer,
    path_convert text,
    position integer,
    status_progress text,
    title text,
    topic_id integer,
    type_id text,
    value text,
    originaUrl text,
    courseID integer,
    download_url text,
    media_version text
  )`,
  local_paths: (name = 'local_paths') => `CREATE TABLE IF NOT EXISTS ${name} (
    id_media integer primary key,
    id_tema integer,
    id_curso integer,
    path text,
    original_url text
  )`,
  benefit_c: (name = 'benefit_c') => `CREATE TABLE IF NOT EXISTS ${name} (
    id_curso integer
  )`,
  offline_podcasts: (name = 'offline_podcasts') => `CREATE TABLE IF NOT EXISTS ${name} (
    media_id integer primary key,
    course_id integer,
    manifest_json text
  )`,
  path_snapshots: (name = 'path_snapshots') => `CREATE TABLE IF NOT EXISTS ${name} (
    course_id integer primary key,
    school_id integer,
    ruta_json text,
    downloaded_at text
  )`,
  path_offline_assets: (name = 'path_offline_assets') => `CREATE TABLE IF NOT EXISTS ${name} (
    course_id integer,
    kind text,
    item_key text,
    payload_json text,
    primary key (course_id, kind, item_key)
  )`,
  mediaSizeControl: (name = 'mediaSizeControl') => `CREATE TABLE IF NOT EXISTS ${name} (
    id integer primary key,
    size integer
  )`,
  evaluations: (name = 'evaluations') => `CREATE TABLE IF NOT EXISTS ${name} (
    attempt text,
    curso_id integer,
    nombre text,
    posteo_id integer,
    preguntas text,
    tipo_evaluacion text,
    evaluation_settings text,
    topicId integer primary key,
    myAnswers text
  )`,
  surveys: (name = 'surveys') => `CREATE TABLE IF NOT EXISTS ${name} (
    enc_preg text,
    curso_id integer primary key
  )`,
};

export type OfflineTableName = keyof typeof OFFLINE_TABLES;

/** Las columnas de `evaluations` y `surveys`, para reconstruirlas con clave primaria. */
const REBUILD_COLUMNS: Record<'evaluations' | 'surveys', string[]> = {
  evaluations: [
    'attempt', 'curso_id', 'nombre', 'posteo_id', 'preguntas', 'tipo_evaluacion',
    'evaluation_settings', 'topicId', 'myAnswers',
  ],
  surveys: ['enc_preg', 'curso_id'],
};

/** Crea la tabla si todavía no existe (la descarga las crea al guardar lo primero). */
export async function ensureTable(db: SqlExecutor, name: OfflineTableName): Promise<void> {
  await db.executeSql(OFFLINE_TABLES[name](), []);
}

async function hasPrimaryKey(db: SqlExecutor, table: string): Promise<boolean> {
  const res = await db.executeSql(`PRAGMA table_info(${table})`, []);
  for (let i = 0; i < res.rows.length; i++) {
    if (Number(res.rows.item(i).pk) > 0) return true;
  }
  return false;
}

/**
 * Le pone clave primaria a una tabla que no la tiene sin perder datos: copia a una tabla nueva (de dos
 * filas con la misma clave queda la más reciente, la de mayor `rowid`), borra la vieja y renombra.
 * Si una corrida anterior se cortó entre borrar y renombrar, termina el renombrado.
 */
async function rebuildWithPrimaryKey(db: SqlExecutor, table: 'evaluations' | 'surveys'): Promise<void> {
  const copy = `${table}_pk`;
  if (!(await tableExists(db, table))) {
    if (await tableExists(db, copy)) await db.executeSql(`ALTER TABLE ${copy} RENAME TO ${table}`, []);
    return;
  }
  if (await hasPrimaryKey(db, table)) return;

  const columns: string[] = [];
  for (const column of REBUILD_COLUMNS[table]) {
    if (await columnExists(db, table, column)) columns.push(column);
  }
  await db.executeSql(`DROP TABLE IF EXISTS ${copy}`, []);
  await db.executeSql(OFFLINE_TABLES[table](copy), []);
  await db.executeSql(
    `INSERT OR REPLACE INTO ${copy} (${columns.join(', ')}) SELECT ${columns.join(', ')} FROM ${table} ORDER BY rowid ASC`,
    []
  );
  await db.executeSql(`DROP TABLE ${table}`, []);
  await db.executeSql(`ALTER TABLE ${copy} RENAME TO ${table}`, []);
}

export const OFFLINE_MIGRATIONS: OfflineMigration[] = [
  {
    version: 1,
    // Antes era `DownloadCourseService.migrateDatabaseSchema()`, que corría en cada descarga.
    description: 'courses: columnas del registro personalizado',
    up: async (db) => {
      await addColumnIfMissing(db, 'courses', 'registro_personalizado_is_active', 'int');
      await addColumnIfMissing(db, 'courses', 'registro_personalizado_path', 'text');
      await addColumnIfMissing(db, 'courses', 'registro_personalizado_url', 'text');
    },
  },
  {
    version: 2,
    // Antes `OfflineService.createPendingRequestTable()` la creaba con solo las 5 primeras columnas.
    description: 'http_requests: la cola nace aquí, con orden, intentos, último error y curso',
    up: async (db) => {
      await db.executeSql(
        `CREATE TABLE IF NOT EXISTS http_requests (
          id text primary key,
          url text,
          method text,
          data text,
          is_sst integer,
          seq integer,
          attempts integer DEFAULT 0,
          last_error text,
          course_id integer
        )`,
        []
      );
      // Si la tabla ya existía (la crearon versiones anteriores), solo faltan las columnas nuevas.
      await addColumnIfMissing(db, 'http_requests', 'seq', 'integer');
      await addColumnIfMissing(db, 'http_requests', 'attempts', 'integer DEFAULT 0');
      await addColumnIfMissing(db, 'http_requests', 'last_error', 'text');
      await addColumnIfMissing(db, 'http_requests', 'course_id', 'integer');
    },
  },
  {
    version: 3,
    description: 'http_requests: completa seq y course_id en las peticiones que ya estaban en la cola',
    up: async (db) => {
      // El orden de las que ya estaban es el de inserción.
      await db.executeSql(`UPDATE http_requests SET seq = rowid WHERE seq IS NULL`, []);

      // El id de la cola lleva el curso en los hitos de curso: `${curso}_survey`, `_sst` y `_cert`.
      for (const suffix of ['_survey', '_sst', '_cert']) {
        await db.executeSql(
          `UPDATE http_requests
             SET course_id = CAST(substr(id, 1, length(id) - ${suffix.length}) AS INTEGER)
           WHERE course_id IS NULL AND substr(id, -${suffix.length}) = ?`,
          [suffix]
        );
      }

      // En los de medio y de evaluación el curso sale de lo descargado, si el curso sigue descargado.
      if (await tableExists(db, 'media')) {
        await db.executeSql(
          `UPDATE http_requests
             SET course_id = (
               SELECT media.courseID FROM media
                WHERE media.id = CAST(substr(http_requests.id, 1, length(http_requests.id) - 13) AS INTEGER)
             )
           WHERE course_id IS NULL AND substr(id, -13) = '_media_status'`,
          []
        );
      }
      if (await tableExists(db, 'topics')) {
        await db.executeSql(
          `UPDATE http_requests
             SET course_id = (
               SELECT topics.course_id FROM topics
                WHERE topics.id = CAST(substr(http_requests.id, 1, length(http_requests.id) - 11) AS INTEGER)
             )
           WHERE course_id IS NULL AND substr(id, -11) = '_evaluation'`,
          []
        );
      }
    },
  },
  {
    version: 4,
    description: 'evaluations y surveys: clave primaria (de las filas repetidas queda la más reciente)',
    up: async (db) => {
      await rebuildWithPrimaryKey(db, 'evaluations');
      await rebuildWithPrimaryKey(db, 'surveys');
    },
  },
  {
    version: 5,
    description: 'http_requests: rejected (el servidor la rechazó para siempre; solo se reenvía a mano)',
    up: async (db) => {
      await addColumnIfMissing(db, 'http_requests', 'rejected', 'integer DEFAULT 0');
    },
  },
  {
    version: 6,
    description: 'courses: format (un curso en ruta se abre en /ruta, no en /tema) y path_snapshots',
    up: async (db) => {
      await addColumnIfMissing(db, 'courses', 'format', 'text');
      await ensureTable(db, 'path_snapshots');
    },
  },
  {
    version: 7,
    description: 'path_offline_assets: lo que una ruta necesita sin conexión y no viaja en /ruta (encuestas, tareas, firmas)',
    up: async (db) => {
      await ensureTable(db, 'path_offline_assets');
    },
  },
];

async function readVersion(db: SqlExecutor): Promise<number> {
  await db.executeSql(
    `CREATE TABLE IF NOT EXISTS ${SCHEMA_VERSION_TABLE} (
      id integer primary key check (id = 1),
      version integer not null
    )`,
    []
  );
  const res = await db.executeSql(`SELECT version FROM ${SCHEMA_VERSION_TABLE} WHERE id = 1`, []);
  return res.rows.length > 0 ? Number(res.rows.item(0).version) || 0 : 0;
}

async function writeVersion(db: SqlExecutor, version: number): Promise<void> {
  await db.executeSql(`INSERT OR REPLACE INTO ${SCHEMA_VERSION_TABLE} (id, version) VALUES (1, ?)`, [version]);
}

/**
 * Corre, en orden, las migraciones que la base todavía no tiene. Si una falla se detiene ahí sin
 * subir la versión, así se vuelve a intentar la próxima vez que se abra la base.
 * Devuelve la versión en la que quedó.
 */
export async function runOfflineMigrations(
  db: SqlExecutor,
  migrations: OfflineMigration[] = OFFLINE_MIGRATIONS
): Promise<number> {
  let current = await readVersion(db);
  const pending = migrations.filter((m) => m.version > current).sort((a, b) => a.version - b.version);

  for (const migration of pending) {
    try {
      await migration.up(db);
      await writeVersion(db, migration.version);
      current = migration.version;
    } catch (error) {
      console.error(`[offline-schema] falló la migración ${migration.version} (${migration.description})`, error);
      break;
    }
  }
  return current;
}
